import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DateTime, TimeStruct, TimeUnit, TimeZone } from 'timezonecomplete';
import { createSelector } from '@reduxjs/toolkit';

import type { ICurrentMonth } from '@/store/types';

import { useAppDispatch, useAppSelector, useTimezone } from '@/hooks';
import { getWeeksArrayByMonth } from '@/store/slices/utilsSlice';
import { selectWeeksByMonth } from '@/store/selectors/utilsSelectors';
import { EVENT_HORIZON } from '@/constants/calendar';

interface IReturn {
  selectedMonth: number | undefined;
  selectedYear: number | undefined;
  isCurrentMonth: boolean;
  selectMonth: (args: ICurrentMonth) => void;
  setNextMonth: () => void;
  setPreviousMonth: () => void;
  canSetNextMonth: boolean;
  canSetPreviousMonth: boolean;
}

const useMonthSelector = createSelector(selectWeeksByMonth, weeksByMonth => ({ weeksByMonth }));

const useMonth = (): IReturn => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { timezoneName } = useTimezone();
  const { weeksByMonth } = useAppSelector(useMonthSelector);
  const [currentDate, setCurrentDate] = useState<{ month: number; year: number } | null>(null);

  const todayDateObject = useMemo<DateTime>(
    () => new DateTime(Date.now(), TimeZone.utc()).convert(TimeZone.zone(timezoneName)),
    [timezoneName],
  );

  /**
   * Read "month" from the URL query param.
   *
   * @return {*}  {(number | null)}
   */
  const _getMonthFromURL = useMemo((): number | null => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('month');

    return queryParam ? Number(queryParam) : null;
  }, [location]);

  /**
   * Read "year" from the URL query param.
   *
   * @return {*}  {(number | null)}
   */
  const _getYearFromURL = useMemo((): number | null => {
    const query = new URLSearchParams(location.search);
    const queryParam = query.get('year');

    return queryParam ? Number(queryParam) : null;
  }, [location]);

  /**
   * Manually select a "month" and "year".
   * Update URL query param for a selected "month" and "year".
   *
   * @param {ICurrentMonth} args
   */
  const selectMonth = useCallback(
    (args: ICurrentMonth) => {
      const { month, year } = args;
      const params = new URLSearchParams(history.location.search);

      params.set('month', String(month));
      params.set('year', String(year));

      history.push({ search: params.toString() });
    },
    [history],
  );

  const _nextMonth = useMemo<DateTime | null>(() => {
    if (!_getMonthFromURL || !_getYearFromURL) return null;

    return new DateTime(
      new TimeStruct({ year: _getYearFromURL, month: _getMonthFromURL, day: todayDateObject.day() }),
      TimeZone.utc(),
    )
      .convert(TimeZone.zone(timezoneName))
      .add(1, TimeUnit.Month);
  }, [_getMonthFromURL, _getYearFromURL, timezoneName, todayDateObject]);

  const _prevMonth = useMemo<DateTime | null>(() => {
    if (!_getMonthFromURL || !_getYearFromURL) return null;

    return new DateTime(
      new TimeStruct({ year: _getYearFromURL, month: _getMonthFromURL, day: todayDateObject.day() }),
      TimeZone.utc(),
    )
      .convert(TimeZone.zone(timezoneName))
      .sub(1, TimeUnit.Month);
  }, [_getMonthFromURL, _getYearFromURL, timezoneName, todayDateObject]);

  const canSetNextMonth = useMemo<boolean>(() => {
    if (!_nextMonth) return false;
    const monthDiff = Math.abs(_nextMonth.diff(todayDateObject).milliseconds());

    return monthDiff <= EVENT_HORIZON;
  }, [_nextMonth, todayDateObject]);

  const canSetPreviousMonth = useMemo((): boolean => {
    if (!_prevMonth) return false;
    const monthDiff = Math.abs(_prevMonth.diff(todayDateObject).milliseconds());

    return monthDiff <= EVENT_HORIZON;
  }, [_prevMonth, todayDateObject]);

  /**
   * Checks whether a manually selected "monthId" (query param)
   * is equal with the current "monthId" (current week).
   * If there's no "monthId" (query param) selected, the expected month is current month.
   *
   * @return {*}  {boolean}
   */
  const isCurrentMonth = useMemo((): boolean => {
    const month = _getMonthFromURL;
    const year = _getYearFromURL;

    if (!month || !year) return false;
    if (!currentDate) return false;

    return month === currentDate.month && year === currentDate.year;
  }, [_getMonthFromURL, _getYearFromURL, currentDate]);

  const setNextMonth = useCallback(() => {
    if (!_nextMonth || !canSetNextMonth) return;

    selectMonth({ month: _nextMonth?.month(), year: _nextMonth?.year() });
  }, [_nextMonth, canSetNextMonth, selectMonth]);

  const setPreviousMonth = useCallback(() => {
    if (!_prevMonth || !canSetPreviousMonth) return;

    selectMonth({ month: _prevMonth.month(), year: _prevMonth.year() });
  }, [_prevMonth, canSetPreviousMonth, selectMonth]);

  useEffect(() => {
    if (_getMonthFromURL && _getYearFromURL) return;

    setCurrentDate({
      // map months from 0-11 to 1-12
      month: todayDateObject.month(),
      year: todayDateObject.year(),
    });
    selectMonth({
      // map months from 0-11 to 1-12
      month: todayDateObject.month(),
      year: todayDateObject.year(),
    });
  }, [_getMonthFromURL, _getYearFromURL, selectMonth, timezoneName, todayDateObject]);

  useEffect(() => {
    if (weeksByMonth) return;
    if (!currentDate) return;

    const promise = dispatch(
      getWeeksArrayByMonth({
        month: String(currentDate.month),
        year: String(currentDate.year),
        tz: encodeURIComponent(timezoneName),
      }),
    );

    return () => promise.abort();
  }, [currentDate, dispatch, timezoneName, weeksByMonth]);

  return {
    isCurrentMonth,
    selectedMonth: _getMonthFromURL || currentDate?.month,
    selectedYear: _getYearFromURL || currentDate?.year,
    selectMonth,
    setNextMonth,
    setPreviousMonth,
    canSetNextMonth,
    canSetPreviousMonth,
  };
};

export { useMonth };
